import React, { useState, useEffect } from 'react';
import {
  Filter, FileSpreadsheet, ChevronDown, Eye, ArrowLeft, Search,
  Play, ArrowDownToLine, Phone, ChevronLeft, ChevronRight,
  Clock, X, Pause, Loader2
} from 'lucide-react';
import { dashboardApi } from '../services/api'; // ✅ api.js se import

// ─── Sub Components ───────────────────────────────────────────────────────────

const StatusCard = ({ label, value, colorClass }) => (
  <div className={`${colorClass} rounded-xl p-4 text-white shadow-sm flex flex-col justify-center min-h-[85px]`}>
    <span className="text-[13px] font-medium opacity-90">{label}</span>
    <span className="text-2xl font-bold mt-1">{value}</span>
  </div>
);

const Spinner = () => (
  <div className="flex items-center justify-center py-16">
    <Loader2 size={32} className="animate-spin text-[#449978]" />
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const AhtBucket = () => {
  const [activeTab, setActiveTab] = useState('Dashboard');
  const [showDetail, setShowDetail] = useState(false);
  const [detailConfig, setDetailConfig] = useState({ type: '', bucket: '' });
  const [openFilter, setOpenFilter] = useState(null);
  const [showPlayer, setShowPlayer] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playerData, setPlayerData] = useState({ phone: '', duration: '' });

  // API state
  const [overview, setOverview] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [grandTotal, setGrandTotal] = useState(null);
  const [ahtBuckets, setAhtBuckets] = useState(null);
  const [ahtCampaignWise, setAhtCampaignWise] = useState(null);
  const [ahtSummary, setAhtSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState('F-Lead');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadDashboardData();
  }, [activeTab, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentPage(0);
  }, [activeTab]);

  // ── Data Fetching — all calls go through dashboardApi (api.js) ─────────────
  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'Dashboard') {
        // 4 parallel calls — all from api.js dashboardApi
        const [ovData, ahtData, cwData, sumData] = await Promise.allSettled([
          dashboardApi.getAnalyticsOverview(),          // /dashboard/analytics/overview
          dashboardApi.getAHTBuckets(),                  // /dashboard/campaign/aht-buckets
          dashboardApi.getAHTCampaignWise(),             // /dashboard/campaign/aht-campaign-wise
          dashboardApi.getAHTCampaignSummary(),          // /dashboard/campaign/aht-summary
        ]);

        if (ovData.status   === 'fulfilled') setOverview(ovData.value);
        if (ahtData.status  === 'fulfilled') setAhtBuckets(ahtData.value);
        if (cwData.status   === 'fulfilled') setAhtCampaignWise(cwData.value);
        if (sumData.status  === 'fulfilled') setAhtSummary(sumData.value);

      } else if (activeTab === 'Employees') {
        // 2 parallel calls
        const [empData, gtData] = await Promise.allSettled([
          dashboardApi.getEmployeePerformance({ page: currentPage, size: 10 }), // /dashboard/analytics/employee-performance
          dashboardApi.getGrandTotal(),                                           // /dashboard/analytics/grand-total
        ]);

        if (empData.status === 'fulfilled') {
          const d = empData.value;
          // Handle { employees:[...], page, size, totalCount } OR Spring Page { content:[...] }
          setEmployees(d?.employees || d?.content || d?.data || (Array.isArray(d) ? d : []));
          const total = d?.totalCount || d?.totalElements || 0;
          const size  = d?.size || 10;
          setTotalPages(d?.totalPages || Math.ceil(total / size) || 1);
        }
        if (gtData.status === 'fulfilled') setGrandTotal(gtData.value);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetail = (type, bucket) => {
    setDetailConfig({ type, bucket });
    setShowDetail(true);
  };

  const handleOpenPlayer = (phone, duration) => {
    setPlayerData({ phone, duration });
    setShowPlayer(true);
    setIsPlaying(false);
  };

  // ── Build status cards from overview ──────────────────────────────────────
  const buildStatusCards = () => {
    if (overview?.campaignSummary || overview?.campaigns) {
      const campaigns = overview.campaignSummary || overview.campaigns || [];
      const colors = ['bg-[#3182CE]','bg-[#ED8936]','bg-[#ECC94B]','bg-[#F687B3]','bg-[#38B2AC]','bg-[#718096]','bg-[#48BB78]','bg-[#D68165]','bg-[#F6AD55]','bg-[#319795]','bg-[#3182CE]','bg-[#9F7AEA]'];
      return campaigns.slice(0, 12).map((c, i) => ({
        label:      c.campaignName || c.name || c.label || `Campaign ${i + 1}`,
        value:      String(c.count || c.total || c.value || 0),
        colorClass: colors[i % colors.length],
      }));
    }
    return [
      { label: 'F-Lead',           value: String(overview?.fLead          ?? '812'), colorClass: 'bg-[#3182CE]' },
      { label: 'RTO-Lead',         value: String(overview?.rtoLead        ?? '232'), colorClass: 'bg-[#ED8936]' },
      { label: 'RTO Dialer',       value: String(overview?.rtoDialer      ?? '133'), colorClass: 'bg-[#ECC94B]' },
      { label: 'Renewal-Lead',     value: String(overview?.renewalLead    ?? '89'),  colorClass: 'bg-[#F687B3]' },
      { label: 'Renewal Dialer',   value: String(overview?.renewalDialer  ?? '87'),  colorClass: 'bg-[#38B2AC]' },
      { label: 'Blaster',          value: String(overview?.blaster        ?? '123'), colorClass: 'bg-[#718096]' },
      { label: 'Customer Service', value: String(overview?.customerService ?? '121'), colorClass: 'bg-[#48BB78]' },
      { label: 'OnBoarding',       value: String(overview?.onBoarding     ?? '112'), colorClass: 'bg-[#D68165]' },
      { label: 'Upsell',           value: String(overview?.upsell         ?? '233'), colorClass: 'bg-[#F6AD55]' },
      { label: 'Verification',     value: String(overview?.verification   ?? '124'), colorClass: 'bg-[#319795]' },
      { label: 'NDR',              value: String(overview?.ndr            ?? '321'), colorClass: 'bg-[#3182CE]' },
      { label: 'OJT',              value: String(overview?.ojt            ?? '123'), colorClass: 'bg-[#9F7AEA]' },
    ];
  };

  // ── AHT chart helpers ─────────────────────────────────────────────────────
  const rawTimeBuckets = Array.isArray(ahtCampaignWise?.timeBuckets)
    ? ahtCampaignWise.timeBuckets
    : Array.isArray(ahtCampaignWise)
    ? ahtCampaignWise
    : [];

  const timeBuckets = rawTimeBuckets.length > 0
    ? rawTimeBuckets.map(b => b.timeRange)
    : ['0-1 min', '1-3 min', '3-5 min', '5-7 min', '7-10 min'];

  const barColors = {
    'Order Placed':   'bg-[#48BB78]',
    'Pending':        'bg-[#ECC94B]',
    'Not interested': 'bg-[#FEB2B2]',
    'Call back':      'bg-[#F6AD55]',
    'Prank call':     'bg-[#718096]',
  };

  const getBarData = (bIdx) => {
    if (rawTimeBuckets.length > 0) {
      const bucket = rawTimeBuckets[bIdx];
      const s = bucket?.statuses || {};
      const values = [
        s['Order Placed'] || 0,
        s['Pending']      || 0,
        s['Not interested'] || 0,
        s['Call back']    || 0,
        s['Prank call']   || 0,
      ];
      const max = Math.max(...values, 1);
      return values.map(v => Math.round((v / max) * 100));
    }
    return [
      [65, 78, 72, 60, 65][bIdx] ?? 50,
      [18, 22, 19, 38, 18][bIdx] ?? 20,
      [10, 58, 10, 20, 10][bIdx] ?? 10,
      [38, 26, 38, 26, 38][bIdx] ?? 30,
      [5,  5,  5,  5,  5 ][bIdx] ?? 5,
    ];
  };

  const getPieData = () => {
    if (ahtSummary) {
      return {
        label:       ahtSummary.campaignName || selectedCampaign,
        total:       ahtSummary.total || ahtSummary.count || 812,
        percentages: ahtSummary.percentages || { orderPlaced: 54.2, pending: 19.6, notInterested: 12.6, callBack: 11.4, prankCall: 4.6 },
      };
    }
    return {
      label:       selectedCampaign,
      total:       812,
      percentages: { orderPlaced: 54.2, pending: 19.6, notInterested: 12.6, callBack: 11.4, prankCall: 4.6 },
    };
  };

  const getEmployeeRows = () => {
    if (employees.length > 0) return employees;
    // Fallback static rows
    return [
      { name: 'Pawan Sharma',      employeeCode: '1272', complete: 0, pending: 326, hold: 134, dispatch: 406, delivered: 101, rto: 75, total: 1777, totalAmount: 723980, avgTotalAmount: 4.16 },
      { name: 'Ashish Ruhela',     employeeCode: '1272', complete: 0, pending: 326, hold: 134, dispatch: 406, delivered: 101, rto: 75, total: 1777, totalAmount: 723980, avgTotalAmount: 4.16 },
      { name: 'Divyanshu Singh',   employeeCode: '1272', complete: 0, pending: 326, hold: 134, dispatch: 406, delivered: 101, rto: 75, total: 1777, totalAmount: 723980, avgTotalAmount: 4.16 },
      { name: 'Anup Mishra',       employeeCode: '1272', complete: 0, pending: 326, hold: 134, dispatch: 406, delivered: 101, rto: 75, total: 1777, totalAmount: 723980, avgTotalAmount: 4.16 },
      { name: 'Pushpender Sharma', employeeCode: '1272', complete: 0, pending: 326, hold: 134, dispatch: 406, delivered: 101, rto: 75, total: 1777, totalAmount: 723980, avgTotalAmount: 4.16 },
      { name: 'Vimal Thakur',      employeeCode: '1272', complete: 0, pending: 326, hold: 134, dispatch: 406, delivered: 101, rto: 75, total: 1777, totalAmount: 723980, avgTotalAmount: 4.16 },
    ];
  };

  const getGrandTotalRow = () => {
    if (grandTotal) {
      return {
        totalEmployees: grandTotal.totalEmployees || grandTotal.empCount          || grandTotal.total     || 0,
        orderPlaced:    grandTotal.complete        || grandTotal.orderPlaced       || 0,
        pending:        grandTotal.pending         || 0,
        hold:           grandTotal.hold            || 0,
        dispatch:       grandTotal.dispatch        || grandTotal.ofd               || 0,
        delivered:      grandTotal.delivered       || 0,
        rto:            grandTotal.rto             || 0,
        total:          grandTotal.total           || grandTotal.totalOrders       || 0,
        totalAmount:    grandTotal.totalAmount     || grandTotal.totalValue        || 0,
        avg:            grandTotal.avgTotalAmount  || grandTotal.avgTotal          || grandTotal.avgValue  || 0,
      };
    }
    return { totalEmployees: 0, orderPlaced: 0, pending: 0, hold: 0, dispatch: 0, delivered: 0, rto: 0, total: 0, totalAmount: 0, avg: 0 };
  };

  const pieData     = getPieData();
  const statusCards = buildStatusCards();

  // ── Detail View ───────────────────────────────────────────────────────────
  if (showDetail) {
    return (
      <div className="p-6 bg-[#F8F9FA] min-h-screen font-sans">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <button onClick={() => setShowDetail(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#449978]">
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-2xl font-bold text-[#2D3748]">{detailConfig.type}</h1>
          </div>
          <div className="flex gap-3">
            <button className="p-2 bg-white rounded-full shadow-sm hover:bg-gray-50 border border-gray-100">
              <Filter size={20} className="text-gray-500" />
            </button>
            <button className="p-2 bg-[#2BD7AD] rounded-full shadow-sm hover:opacity-90 transition-opacity">
              <FileSpreadsheet size={20} className="text-white" />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 max-w-[300px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type="text" placeholder="Search by mobile number or customer name" className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#449978]/20" />
          </div>
          <div className="relative">
            <button onClick={() => setOpenFilter(openFilter === 'status' ? null : 'status')} className="flex items-center gap-10 px-4 py-2 border border-gray-200 rounded-full text-sm text-gray-600 bg-white hover:bg-gray-50 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-yellow-400" />
                <span className="font-medium text-gray-700">{detailConfig.type || 'Order placed'}</span>
              </div>
              <ChevronDown size={16} className={`transition-transform ${openFilter === 'status' ? 'rotate-180' : ''}`} />
            </button>
            {openFilter === 'status' && (
              <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-100 rounded-xl shadow-xl z-50 overflow-hidden py-1">
                {['Order Placed', 'Call Back', 'Not Interested'].map(opt => (
                  <div key={opt} onClick={() => { setDetailConfig({ ...detailConfig, type: opt }); setOpenFilter(null); }} className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${opt === detailConfig.type ? 'bg-[#f0fff4] text-[#449978] font-bold' : 'text-gray-600 hover:bg-[#f0fff4]'}`}>{opt}</div>
                ))}
              </div>
            )}
          </div>
          <div className="relative">
            <button onClick={() => setOpenFilter(openFilter === 'time' ? null : 'time')} className="flex items-center gap-10 px-4 py-2 border border-gray-200 rounded-full text-sm text-gray-600 bg-white hover:bg-gray-50 shadow-sm">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-orange-400" />
                <span className="font-medium text-gray-700">{detailConfig.bucket}</span>
              </div>
              <ChevronDown size={16} className={`transition-transform ${openFilter === 'time' ? 'rotate-180' : ''}`} />
            </button>
            {openFilter === 'time' && (
              <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-100 rounded-xl shadow-xl z-50 overflow-hidden py-1">
                {['0-1 MIN', '1-3 min', '3-5 min', '5-7 min', '7-10 min'].map(opt => (
                  <div key={opt} onClick={() => { setDetailConfig({ ...detailConfig, bucket: opt }); setOpenFilter(null); }} className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${opt === detailConfig.bucket ? 'bg-[#f0fff4] text-[#449978] font-bold' : 'text-gray-600 hover:bg-[#f0fff4]'}`}>{opt}</div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl shadow-md border border-gray-200 bg-white">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#449978] text-white text-[10px] uppercase tracking-widest">
                <th className="px-4 py-4 font-bold border-r border-white/20">S. L. No.</th>
                <th className="px-4 py-4 font-bold border-r border-white/20">DISPOSED BY</th>
                <th className="px-4 py-4 font-bold border-r border-white/20">NAME</th>
                <th className="px-4 py-4 font-bold border-r border-white/20">DATE</th>
                <th className="px-4 py-4 font-bold border-r border-white/20">DURATION</th>
                <th className="px-4 py-4 font-bold border-r border-white/20">REMARKS</th>
                <th className="px-4 py-4 font-bold">ACTION</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {[
                { sNo: 1,  dispBy: 'Manisha Singh',       empId: '1239', name: 'Hemant Mehto',       phone: 'XXXXXX2987', date: '23/03/2024, 05:09 PM', dur: '00m 56s', rem: 'Order Placed' },
                { sNo: 2,  dispBy: 'Dilip Singh Bisht',   empId: '1123', name: 'Sumit Kumar Upa...',  phone: 'XXXXXX2987', date: '23/03/2024, 04:33 PM', dur: '00m 45s', rem: 'Order Placed' },
                { sNo: 3,  dispBy: 'Nalini Singh',         empId: '1234', name: 'Dilip Topi',          phone: 'XXXXXX2987', date: '23/03/2024, 04:41 PM', dur: '00m 48s', rem: 'Order Placed' },
                { sNo: 4,  dispBy: 'Laxmi Singh',          empId: '3912', name: 'Deep Singh',           phone: 'XXXXXX2987', date: '23/03/2024, 03:12 PM', dur: '00m 46s', rem: 'Order Placed' },
                { sNo: 5,  dispBy: 'Prashant Kumar M...',  empId: '5433', name: 'Sunita Goswami',       phone: 'XXXXXX2987', date: '23/03/2024, 02:50 PM', dur: '00m 05s', rem: 'Order Placed' },
                { sNo: 6,  dispBy: 'Karan Singh',           empId: '9800', name: 'Ruchi Bhardwaj',       phone: 'XXXXXX2987', date: '23/03/2024, 02:02 PM', dur: '00m 12s', rem: 'Order Placed' },
                { sNo: 7,  dispBy: 'Vipin Kumar Mishr...', empId: '7677', name: 'Rohit Mehra',           phone: 'XXXXXX2987', date: '23/03/2024, 01:09 PM', dur: '00m 34s', rem: 'Order Placed' },
                { sNo: 8,  dispBy: 'Vijay Shankar',         empId: '1112', name: 'Dinesh Lal',            phone: 'XXXXXX2987', date: '23/03/2024, 12:15 PM', dur: '00m 39s', rem: 'Order Placed' },
                { sNo: 9,  dispBy: 'Sanjeev Kumar',         empId: '0987', name: 'Sumit Kumar Upa...',  phone: 'XXXXXX2987', date: '23/03/2024, 11:12 AM', dur: '00m 53s', rem: 'Order Placed' },
              ].map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 border-r border-gray-100 text-center">{row.sNo}</td>
                  <td className="px-4 py-3 border-r border-gray-100">
                    <div className="font-bold text-[#2D3748]">{row.dispBy}</div>
                    <div className="text-[10px] text-gray-400">Emp: {row.empId}</div>
                  </td>
                  <td className="px-4 py-3 border-r border-gray-100">
                    <div className="font-bold text-[#2D3748]">{row.name}</div>
                    <div className="flex items-center gap-1 text-[10px] text-gray-400">
                      <Phone size={10} className="text-[#449978]" /> {row.phone}
                    </div>
                  </td>
                  <td className="px-4 py-4 border-r border-gray-100 text-gray-600">{row.date}</td>
                  <td className="px-4 py-4 border-r border-gray-100 text-gray-600">{row.dur}</td>
                  <td className="px-4 py-4 border-r border-gray-100 font-medium text-gray-600">{row.rem}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleOpenPlayer(row.phone, row.dur)} className="w-7 h-7 bg-[#559E81] rounded-full flex items-center justify-center text-white shadow-sm hover:opacity-90">
                        <Play size={14} fill="white" />
                      </button>
                      {row.sNo !== 8 && (
                        <button className="w-7 h-7 bg-[#2BD7AD] rounded-full flex items-center justify-center text-white shadow-sm hover:opacity-90">
                          <ArrowDownToLine size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between items-center mt-6 text-[13px]">
          <div className="text-gray-500">Showing <span className="font-bold text-gray-800">9/46</span> entries</div>
          <div className="flex items-center gap-1">
            <button className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-400"><ChevronLeft size={18} /></button>
            {[1, 2, 3, 4, 5].map(p => (
              <button key={p} className={`w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 transition-colors ${p === 1 ? 'bg-[#449978] text-white border-[#449978]' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{p}</button>
            ))}
            <button className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-[#449978]"><ChevronRight size={18} /></button>
          </div>
        </div>

        {showPlayer && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-[24px] w-full max-w-[360px] overflow-hidden shadow-2xl">
              <div className="bg-[#C1F1E1] px-6 py-4 flex justify-between items-center">
                <span className="font-bold text-[#2D3748]">{playerData.phone}</span>
                <button onClick={() => setShowPlayer(false)} className="text-gray-600 hover:text-gray-800 transition-colors"><X size={20} /></button>
              </div>
              <div className="p-6">
                <div className="bg-[#F2FBF8] rounded-2xl p-6 border border-[#E3F5EE] relative overflow-hidden">
                  <div className="text-[11px] font-semibold text-[#2D3748] mb-8">Call Duration: {playerData.duration}</div>
                  <div className="h-20 flex items-center justify-center mb-6 relative">
                    <style>{`@keyframes movePlayhead{0%{left:0%}100%{left:100%}}@keyframes pulsateWave{0%,100%{transform:scaleY(1)}50%{transform:scaleY(1.2)}}.animate-playing-wave{animation:pulsateWave 0.5s ease-in-out infinite;transform-origin:center}.animate-playhead{animation:movePlayhead 10s linear infinite}`}</style>
                    <svg viewBox="0 0 200 40" className={`w-full h-full text-[#449978] opacity-60 ${isPlaying ? 'animate-playing-wave' : ''}`}>
                      <path d="M0 20 L40 20 L45 15 L50 25 L55 5 L60 35 L65 15 L70 25 L75 20 L110 20 L115 10 L120 30 L125 15 L130 25 L135 20 L200 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className={`absolute top-0 bottom-0 w-[1.5px] bg-[#449978] opacity-50 ${isPlaying ? 'animate-playhead' : 'left-[20%]'}`} />
                  </div>
                  <div className="flex flex-col items-center gap-4">
                    <button onClick={() => setIsPlaying(!isPlaying)} className="w-16 h-16 rounded-full flex items-center justify-center text-white shadow-lg hover:scale-105 transition-transform bg-[#559E81]">
                      {isPlaying ? <Pause size={28} fill="white" /> : <Play size={28} className="translate-x-1" fill="white" />}
                    </button>
                    <div className="text-[17px] font-bold text-[#2D3748] tracking-widest">00 : 49</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const gt = getGrandTotalRow();

  // ── Main View ─────────────────────────────────────────────────────────────
  return (
    <div className="p-6 bg-[#F8F9FA] min-h-screen">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-[#2D3748]">AHT bucket</h1>
        {error && (
          <span className="text-xs text-red-500 bg-red-50 px-3 py-1 rounded-full border border-red-200">
            API error — showing fallback data
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="flex justify-between items-end border-b border-gray-200 mb-6">
        <div className="flex gap-8">
          {['Dashboard', 'Employees', 'Dispositions'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`pb-2 text-[15px] font-semibold transition-all relative ${activeTab === tab ? 'text-[#449978]' : 'text-gray-500 hover:text-gray-700'}`}>
              {tab}
              {activeTab === tab && <div className="absolute bottom-0 left-0 w-full h-[2px] bg-[#449978]" />}
            </button>
          ))}
        </div>
        {(activeTab === 'Employees' || activeTab === 'Dispositions') && (
          <div className="mb-2">
            <button className="flex items-center gap-10 px-4 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 bg-white hover:bg-gray-50 shadow-sm">
              {selectedCampaign} <ChevronDown size={16} />
            </button>
          </div>
        )}
      </div>

      {loading && <Spinner />}

      {/* ── Dashboard Tab ── */}
      {!loading && activeTab === 'Dashboard' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-8">
            {statusCards.map((card, index) => <StatusCard key={index} {...card} />)}
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Bar Chart */}
            <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-[17px] font-bold text-[#2D3748]">AHT campaign wise</h2>
                <button className="flex items-center gap-6 px-4 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 bg-white hover:bg-gray-50">
                  {selectedCampaign} <ChevronDown size={16} />
                </button>
              </div>
              <div className="flex flex-wrap gap-6 justify-center mb-10 text-[12px] font-medium text-gray-600">
                {Object.entries(barColors).map(([label, color]) => (
                  <div key={label} className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-sm ${color}`} />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
              <div className="relative h-[300px] mt-10 ml-8">
                <div className="absolute left-[-40px] h-full flex flex-col justify-between text-[11px] text-gray-400">
                  <span>100</span><span>80</span><span>60</span><span>40</span><span>20</span><span>00</span>
                </div>
                <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                  {[0,1,2,3,4,5].map(i => <div key={i} className="w-full border-t border-gray-100" />)}
                </div>
                <div className="absolute inset-0 flex justify-between px-10 items-end">
                  {timeBuckets.map((bucket, bIdx) => {
                    const [h1, h2, h3, h4, h5] = getBarData(bIdx);
                    return (
                      <div key={bucket} className="flex flex-col items-center flex-1">
                        <div className="flex items-end gap-1 mb-2 h-[220px]">
                          <div className="w-[6px] bg-[#48BB78] rounded-t-sm" style={{ height: `${h1}%` }} />
                          <div className="w-[6px] bg-[#ECC94B] rounded-t-sm" style={{ height: `${h2}%` }} />
                          <div className="w-[6px] bg-[#FEB2B2] rounded-t-sm" style={{ height: `${h3}%` }} />
                          <div className="w-[6px] bg-[#F6AD55] rounded-t-sm" style={{ height: `${h4}%` }} />
                          <div className="w-[6px] bg-[#718096] rounded-t-sm" style={{ height: `${h5}%` }} />
                        </div>
                        <span className="text-[11px] text-gray-500 font-medium">{bucket}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Donut Chart */}
            <div className="lg:w-[380px] bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-[17px] font-bold text-[#2D3748] mb-8">AHT campaign summary</h2>
              <div className="relative flex items-center justify-center py-10">
                <svg viewBox="0 0 100 100" className="w-[280px] h-[280px] transform -rotate-90">
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="#48BB78" strokeWidth="12" strokeDasharray="340.5 1000" />
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="#ECC94B" strokeWidth="12" strokeDasharray="28.6 1000"  strokeDashoffset="-136" />
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="#FEB2B2" strokeWidth="12" strokeDasharray="31.6 1000"  strokeDashoffset="-164" />
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="#F6AD55" strokeWidth="12" strokeDasharray="49.2 1000"  strokeDashoffset="-196" />
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="#718096" strokeWidth="12" strokeDasharray="11.5 1000"  strokeDashoffset="-245" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="bg-[#449978] text-white rounded-full w-[130px] h-[130px] flex flex-col items-center justify-center shadow-lg border-[8px] border-white">
                    <span className="text-[13px] opacity-90">{pieData.label}</span>
                    <span className="text-[34px] font-bold leading-none mt-1">{pieData.total}</span>
                  </div>
                </div>
                <span className="absolute right-[12%] top-[50%]  text-[11px] font-bold text-[#2D3748]">{pieData.percentages.orderPlaced}%</span>
                <span className="absolute bottom-[18%] left-[28%] text-[11px] font-bold text-[#2D3748]">{pieData.percentages.callBack}%</span>
                <span className="absolute bottom-[20%] left-[8%]  text-[11px] font-bold text-[#2D3748]">{pieData.percentages.notInterested}%</span>
                <span className="absolute top-[20%] left-[18%]   text-[11px] font-bold text-[#2D3748]">{pieData.percentages.pending}%</span>
                <span className="absolute top-[12%] right-[42%]  text-[11px] font-bold text-[#2D3748]">{pieData.percentages.prankCall}%</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Employees Tab ── */}
      {!loading && activeTab === 'Employees' && (
        <>
          <div className="overflow-x-auto rounded-xl shadow-md border border-gray-200 bg-white">
            <table className="w-full text-left border-collapse min-w-[1000px]">
              <thead>
                <tr className="bg-[#449978] text-white text-[10px] uppercase tracking-widest">
                  <th className="px-6 py-4 font-bold border-r border-white/20">NAME</th>
                  <th className="px-4 py-4 font-bold border-r border-white/20">EMP CODE</th>
                  <th className="px-4 py-4 font-bold border-r border-white/20">COMPLETE</th>
                  <th className="px-4 py-4 font-bold border-r border-white/20">PENDING</th>
                  <th className="px-4 py-4 font-bold border-r border-white/20">HOLD</th>
                  <th className="px-4 py-4 font-bold border-r border-white/20">DISPATCH</th>
                  <th className="px-4 py-4 font-bold border-r border-white/20">DELIVERED</th>
                  <th className="px-4 py-4 font-bold border-r border-white/20">RTO</th>
                  <th className="px-4 py-4 font-bold border-r border-white/20">TOTAL</th>
                  <th className="px-4 py-4 font-bold border-r border-white/20">TOTAL AMOUNT</th>
                  <th className="px-4 py-4 font-bold">AVG AMOUNT</th>
                </tr>
              </thead>
              <tbody className="text-[13px] text-[#4A5568]">
                {getEmployeeRows().map((row, index) => {
                  const name        = row.name         || row.employeeName    || `Employee ${index + 1}`;
                  const empCode     = row.employeeCode  || row.employeeId      || row.empId || '—';
                  const complete    = row.complete      ?? 0;
                  const pending     = row.pending       ?? 0;
                  const hold        = row.hold          ?? 0;
                  const dispatch    = row.dispatch      ?? 0;
                  const delivered   = row.delivered     ?? 0;
                  const rto         = row.rto           ?? 0;
                  const total       = row.total         ?? 0;
                  const totalAmount = row.totalAmount   ?? 0;
                  const avg         = Number(row.avgTotalAmount ?? row.avgTotal ?? 0).toFixed(2);
                  return (
                    <tr key={index} className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-[#F8FBF9]'} hover:bg-gray-50`}>
                      <td className="px-6 py-4 font-semibold text-[#2D3748] border-r border-gray-100">{name}</td>
                      <td className="px-4 py-4 border-r border-gray-100 text-gray-500">{empCode}</td>
                      <td className="px-4 py-4 border-r border-gray-100 text-center">{complete}</td>
                      <td className="px-4 py-4 border-r border-gray-100 text-center">{pending}</td>
                      <td className="px-4 py-4 border-r border-gray-100 text-center">{hold}</td>
                      <td className="px-4 py-4 border-r border-gray-100 text-center">{dispatch}</td>
                      <td className="px-4 py-4 border-r border-gray-100 text-center">{delivered}</td>
                      <td className="px-4 py-4 border-r border-gray-100 text-center">{rto}</td>
                      <td className="px-4 py-4 border-r border-gray-100 text-center font-bold text-[#2D3748]">{total}</td>
                      <td className="px-4 py-4 border-r border-gray-100 text-center">₹{Number(totalAmount).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-4 text-center font-medium text-[#2D3748]">₹{avg}</td>
                    </tr>
                  );
                })}

                {/* Grand Total Row */}
                <tr className="bg-[#f0fff4]/50 font-bold text-[#2D3748] border-t-2 border-[#449978]/20">
                  <td className="px-6 py-5 border-r border-gray-200">Grand Total</td>
                  <td className="px-4 py-5 border-r border-gray-200 text-gray-500">{gt.totalEmployees}</td>
                  <td className="px-4 py-5 border-r border-gray-200 text-center">{gt.orderPlaced}</td>
                  <td className="px-4 py-5 border-r border-gray-200 text-center">{gt.pending}</td>
                  <td className="px-4 py-5 border-r border-gray-200 text-center">{gt.hold}</td>
                  <td className="px-4 py-5 border-r border-gray-200 text-center">{gt.dispatch}</td>
                  <td className="px-4 py-5 border-r border-gray-200 text-center">{gt.delivered}</td>
                  <td className="px-4 py-5 border-r border-gray-200 text-center">{gt.rto}</td>
                  <td className="px-4 py-5 border-r border-gray-200 text-center font-extrabold text-black">{gt.total}</td>
                  <td className="px-4 py-5 border-r border-gray-200 text-center text-[#449978]">₹{Number(gt.totalAmount).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-5 text-center text-[#449978] font-black">₹{Number(gt.avg).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center mt-6 text-[13px]">
            <div className="text-gray-500">
              Showing <span className="font-bold text-gray-800">{employees.length}/{totalPages * 10}</span> entries
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0}
                className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-400 disabled:opacity-40">
                <ChevronLeft size={18} />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const pg = totalPages <= 5 ? i : Math.max(0, currentPage - 2) + i;
                if (pg >= totalPages) return null;
                return (
                  <button key={pg} onClick={() => setCurrentPage(pg)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg border transition-colors ${currentPage === pg ? 'bg-[#449978] text-white border-[#449978]' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                    {pg + 1}
                  </button>
                );
              })}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage >= totalPages - 1}
                className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-[#449978] disabled:opacity-40">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Dispositions Tab ── */}
      {!loading && activeTab === 'Dispositions' && (
        <div className="overflow-x-auto rounded-xl shadow-md border border-gray-200 bg-white">
          <table className="w-full text-left border-collapse min-w-[1000px]">
            <thead>
              <tr className="bg-[#449978] text-white text-[10px] uppercase tracking-widest">
                <th className="px-6 py-4 font-bold border-r border-white/20">DISPOSITIONS TYPE</th>
                <th className="px-4 py-4 font-bold border-r border-white/20">0-1 MIN</th>
                <th className="px-4 py-4 font-bold border-r border-white/20">1-3 MIN</th>
                <th className="px-4 py-4 font-bold border-r border-white/20">3-5 MIN</th>
                <th className="px-4 py-4 font-bold border-r border-white/20">5-7 MIN</th>
                <th className="px-4 py-4 font-bold border-r border-white/20">7-10 MIN</th>
                <th className="px-4 py-4 font-bold">AVG TALK TIME</th>
              </tr>
            </thead>
            <tbody className="text-[13px] text-[#4A5568]">
              {(ahtBuckets?.dispositions || [
                { type: 'Order Placed',   t1: '832', t2: '712', t3: '326', t4: '134', t5: '232', avg: '07m 12s' },
                { type: 'Not Interested', t1: '412', t2: '301', t3: '223', t4: '21',  t5: '134', avg: '03m 56s' },
                { type: 'Call Back',      t1: '372', t2: '112', t3: '123', t4: '21',  t5: '80',  avg: '07m 12s' },
              ]).map((row, index) => (
                <tr key={index} className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-[#F8FBF9]'}`}>
                  <td className="px-6 py-4 font-semibold text-[#2D3748] border-r border-gray-100">
                    {row.type || row.dispositionType}
                  </td>
                  {[row.t1 ?? row.bucket1, row.t2 ?? row.bucket2, row.t3 ?? row.bucket3, row.t4 ?? row.bucket4, row.t5 ?? row.bucket5].map((val, ti) => (
                    <td key={ti} className="px-4 py-4 border-r border-gray-100">
                      <div className="flex items-center gap-2">
                        {val ?? 0}
                        <Eye size={14} className="text-[#449978] cursor-pointer"
                          onClick={() => handleOpenDetail(row.type || row.dispositionType, ['0-1 MIN','1-3 MIN','3-5 MIN','5-7 MIN','7-10 MIN'][ti])} />
                      </div>
                    </td>
                  ))}
                  <td className="px-4 py-4 font-medium text-[#2D3748]">{row.avg || row.avgTalkTime || '—'}</td>
                </tr>
              ))}
              <tr className="h-40 border-b border-gray-100"><td colSpan={7}></td></tr>
              <tr className="bg-[#f0fff4]/50 font-bold text-[#2D3748] border-t-2 border-[#449978]/20">
                <td className="px-6 py-5 border-r border-gray-200">Total</td>
                <td className="px-4 py-5 border-r border-gray-200">{ahtBuckets?.totals?.t1 ?? 1616}</td>
                <td className="px-4 py-5 border-r border-gray-200">{ahtBuckets?.totals?.t2 ?? 1125}</td>
                <td className="px-4 py-5 border-r border-gray-200">{ahtBuckets?.totals?.t3 ?? 672}</td>
                <td className="px-4 py-5 border-r border-gray-200">{ahtBuckets?.totals?.t4 ?? 176}</td>
                <td className="px-4 py-5 border-r border-gray-200">{ahtBuckets?.totals?.t5 ?? 446}</td>
                <td className="px-4 py-5 bg-[#f0fff4] font-black">{ahtBuckets?.totals?.avg ?? '4.19'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AhtBucket;